import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getHealthAssessmentToolboxPath,
  HEALTH_ASSESSMENT_PROMPT_DISMISSED_KEY,
} from 'common/health-assessment';

const hasDismissedHealthAssessmentPrompt = (): boolean =>
  window.localStorage.getItem(HEALTH_ASSESSMENT_PROMPT_DISMISSED_KEY) ===
  'true';

const markHealthAssessmentPromptDismissed = () => {
  window.localStorage.setItem(HEALTH_ASSESSMENT_PROMPT_DISMISSED_KEY, 'true');
};

type UseHealthAssessmentPromptOptions = {
  isOnboardingReady?: boolean;
  shouldShowFeatureHighlights?: boolean;
};

export const useHealthAssessmentPrompt = ({
  isOnboardingReady = false,
  shouldShowFeatureHighlights = false,
}: UseHealthAssessmentPromptOptions = {}) => {
  const navigate = useNavigate();
  const [
    shouldShowHealthAssessmentPrompt,
    setShouldShowHealthAssessmentPrompt,
  ] = useState(false);

  const showPromptIfNeeded = useCallback(() => {
    if (!hasDismissedHealthAssessmentPrompt()) {
      setShouldShowHealthAssessmentPrompt(true);
    }
  }, []);

  useEffect(() => {
    if (
      isOnboardingReady &&
      !shouldShowFeatureHighlights &&
      !hasDismissedHealthAssessmentPrompt()
    ) {
      setShouldShowHealthAssessmentPrompt(true);
    }
  }, [isOnboardingReady, shouldShowFeatureHighlights]);

  const onOnboardingComplete = useCallback(() => {
    showPromptIfNeeded();
  }, [showPromptIfNeeded]);

  const onStartHealthAssessment = useCallback(() => {
    markHealthAssessmentPromptDismissed();
    setShouldShowHealthAssessmentPrompt(false);
    navigate(getHealthAssessmentToolboxPath());
  }, [navigate]);

  const onDismissHealthAssessmentPrompt = useCallback(() => {
    markHealthAssessmentPromptDismissed();
    setShouldShowHealthAssessmentPrompt(false);
  }, []);

  return {
    shouldShowHealthAssessmentPrompt,
    onOnboardingComplete,
    onStartHealthAssessment,
    onDismissHealthAssessmentPrompt,
  };
};
